import { lazy, Suspense, useEffect, useMemo, useState } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { authClient } from "@/lib/auth";
import { ApiError, api } from "@/lib/api";
import type { AdminMeResponse } from "@/types";
import { AppShell } from "@/components/layout/AppShell";
import { LoadingState } from "@/components/data/LoadingState";
import { LoginPage } from "@/pages/LoginPage";
import { OverviewPage } from "@/pages/OverviewPage";
import { UsersPage } from "@/pages/UsersPage";
import { UserDetailPage } from "@/pages/UserDetailPage";
import { ErrorsPage } from "@/pages/ErrorsPage";
import { ActivityPage } from "@/pages/ActivityPage";
import { MessagesPage } from "@/pages/MessagesPage";
import { FeedbackPage } from "@/pages/FeedbackPage";
import { NotificationsPage } from "@/pages/NotificationsPage";
import { SubscriptionsPage } from "@/pages/SubscriptionsPage";
import { SubscriptionDetailPage } from "@/pages/SubscriptionDetailPage";
import { SystemPage } from "@/pages/SystemPage";
import { AuditPage } from "@/pages/AuditPage";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ShieldX } from "lucide-react";

const UsersMapPage = lazy(async () => {
  const mod = await import("@/pages/UsersMapPage");
  return { default: mod.UsersMapPage };
});

interface AdminMe {
  id: string;
  email: string;
  name?: string | null;
  image?: string | null;
}

export default function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const session = authClient.useSession();
  const [forbidden, setForbidden] = useState(false);

  const me: AdminMe | null = useMemo(() => {
    const u = session.data?.user as { id?: string; email?: string; name?: string | null; image?: string | null } | undefined;
    if (!u || !u.id || !u.email) return null;
    return { id: u.id, email: u.email, name: u.name ?? null, image: u.image ?? null };
  }, [session.data]);

  // Probe admin access against the API once session resolves.
  const adminProbe = useQuery({
    queryKey: ["admin-probe", me?.id],
    enabled: !!me,
    retry: false,
    staleTime: 60_000,
    queryFn: async ({ signal }) => {
      try {
        // A cheap endpoint: requireAdmin does the real check.
        await api.get<AdminMeResponse>("/api/admin/me", undefined, signal);
        return { ok: true } as const;
      } catch (err) {
        if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
          return { ok: false, status: err.status } as const;
        }
        throw err;
      }
    },
  });

  useEffect(() => {
    if (adminProbe.data && !adminProbe.data.ok) setForbidden(true);
    else if (adminProbe.data?.ok) setForbidden(false);
  }, [adminProbe.data]);

  // Redirect logic
  useEffect(() => {
    if (session.isPending) return;
    if (!me && location.pathname !== "/login") {
      navigate(`/login?next=${encodeURIComponent(location.pathname + location.search)}`, { replace: true });
    } else if (me && location.pathname === "/login") {
      // Honour ?next= from the redirect above, but only same-origin paths.
      const next = new URLSearchParams(location.search).get("next");
      const safeNext = next && next.startsWith("/") && !next.startsWith("//") && !next.startsWith("/login") ? next : "/";
      navigate(safeNext, { replace: true });
    }
  }, [me, location, navigate, session.isPending]);

  if (session.isPending) {
    return (
      <div className="grid h-screen w-screen place-items-center bg-background">
        <LoadingState label="Authenticating…" />
      </div>
    );
  }

  if (!me) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  if (adminProbe.isPending) {
    return (
      <div className="grid h-screen w-screen place-items-center bg-background">
        <LoadingState label="Verifying admin access…" />
      </div>
    );
  }

  if (forbidden || (adminProbe.data && !adminProbe.data.ok)) {
    return <NotAdmin email={me.email} />;
  }

  return (
    <Routes>
      <Route path="/login" element={<Navigate to="/" replace />} />
      <Route element={<AppShell user={me} />}>
        <Route index element={<OverviewPage />} />
        <Route path="users" element={<UsersPage />} />
        <Route
          path="map"
          element={
            <Suspense fallback={<LoadingState label="Loading map…" />}>
              <UsersMapPage />
            </Suspense>
          }
        />
        <Route path="users/:id" element={<UserDetailPage />} />
        <Route path="errors" element={<ErrorsPage />} />
        <Route path="activity" element={<ActivityPage />} />
        <Route path="messages" element={<MessagesPage />} />
        <Route path="feedback" element={<FeedbackPage />} />
        <Route path="subscriptions" element={<SubscriptionsPage />} />
        <Route path="subscriptions/:id" element={<SubscriptionDetailPage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="system" element={<SystemPage />} />
        <Route path="audit" element={<AuditPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

function NotAdmin({ email }: { email: string }) {
  const navigate = useNavigate();
  return (
    <div className="grid h-screen w-screen place-items-center bg-background p-6">
      <Card className="w-full max-w-md p-6 text-center">
        <div className="mx-auto mb-3 grid h-10 w-10 place-items-center rounded-full bg-rose-500/10 text-rose-300">
          <ShieldX className="h-5 w-5" />
        </div>
        <h1 className="text-xl font-semibold">Not an admin</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          You're signed in as <span className="font-mono">{email}</span>, but this account does not have admin access.
        </p>
        <div className="mt-5 flex justify-center gap-2">
          <Button
            variant="outline"
            onClick={async () => {
              await authClient.signOut();
              navigate("/login", { replace: true });
            }}
          >
            Sign out
          </Button>
        </div>
      </Card>
    </div>
  );
}
